import { useCallback, useState } from 'react';

import type IAiChatSession from '../interfaces/IAiChatSession';
import type IAiChatSessionState from '../interfaces/IAiChatSessionState';

const INITIAL_SESSION: IAiChatSessionState = { chatId: undefined, conversationKey: 0 };

/** A move onto another chat, or off the current one: the conversation starts over. */
const moveTo = (current: IAiChatSessionState, chatId?: number): IAiChatSessionState => ({
  chatId,
  conversationKey: current.conversationKey + 1,
});

/**
 * The chat a caller stays on for as long as it is mounted, and the conversation that shows it.
 *
 * The first message creates the chat and every message after it joins the same one, which is what
 * keeps the messages in one history the API can read back. The reply names the chat it was
 * written into on its `start` chunk, and the session adopts that id.
 *
 * The conversation key tells the chat when to start over. Adopting a created chat keeps it: the
 * conversation that streamed the first reply goes on, so the reply stays on screen.
 *
 * The chat is not stored anywhere, so a reload starts a new one.
 */
export default function useAiChatSession(): IAiChatSession {
  const [session, setSession] = useState<IAiChatSessionState>(INITIAL_SESSION);

  const adoptCreatedChat = useCallback((createdChatId: number) => {
    setSession((current) => ({ ...current, chatId: createdChatId }));
  }, []);

  // Reading an older chat is the same move as adopting a new one: every later message joins it.
  // Picking the chat the session is on already changes nothing, so the conversation goes on.
  const selectChat = useCallback((nextChatId: number) => {
    setSession((current) => (current.chatId === nextChatId ? current : moveTo(current, nextChatId)));
  }, []);

  // No chat is the state the session starts in, so leaving it is all a new chat takes. Nothing is
  // written until a message is sent, which keeps the history free of chats that were never used.
  const startNewChat = useCallback(() => {
    setSession((current) => moveTo(current, undefined));
  }, []);

  // A deleted chat answers every later message with a 403, so a session on it moves off. A session
  // on any other chat is not touched.
  const dropChat = useCallback((deletedChatId: number) => {
    setSession((current) => (current.chatId === deletedChatId ? moveTo(current, undefined) : current));
  }, []);

  return {
    chatId: session.chatId,
    conversationKey: session.conversationKey,
    adoptCreatedChat,
    selectChat,
    startNewChat,
    dropChat,
  };
}
